import React, { useEffect, useState } from "react";
import { Card, Collection } from "@emisgroup/ui-card";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { FormElement } from "../../../Common/UI/Index";
import { IExecutionConfiguration, ConfigurationType, ISiteConfiguration } from "./Types";
import { RelativeDatePopup } from "./RelativeDatePopup";
import { Practices } from "./Practices";
import { MultiSiteEntry } from "./MultiSiteEntry";
import User from "~icons/ic/outline-person";
import "./Style.scss";

interface SummaryViewProps {
  executionConfiguration: IExecutionConfiguration;
  showingRunDates: boolean;
  showSelection?: boolean;
}

export const SummaryView: React.FC<SummaryViewProps> = ({ executionConfiguration, showingRunDates, showSelection }) => {
  const { t: translate } = useTranslation();
  const [siteList, setSiteList] = useState<ISiteConfiguration[]>([]);

  useEffect(() => {
    setSiteList(executionConfiguration.sites ?? []);
  }, [executionConfiguration.sites]);

  const configurationTypeView = React.useMemo<Record<ConfigurationType, React.ReactNode>>(() => ({
    [ConfigurationType.SITES]: (
      <MultiSiteEntry siteList={siteList} showSelection={showSelection} />
    ),
    [ConfigurationType.CDB]: (
      <Practices
        key={executionConfiguration.dsa?.id}
        allowSelection={showSelection}
        executionConfiguration={executionConfiguration}
        dsa={ executionConfiguration.dsa?.id as string || ""}
      />
    ),
    [ConfigurationType.DSA]: (
      <Practices
        key={executionConfiguration.dsa?.id}
        allowSelection={showSelection}
        executionConfiguration={executionConfiguration}
        dsa={ executionConfiguration.dsa?.id as string || ""}
      />
    ),
  }), [siteList, showSelection, executionConfiguration]);


  return (
    <div className="summary-view">
      <Collection>
        <Card outline className="summary-view__cards">
          <div>
            <FormElement
              labelText={translate("ViewExecution.Author")}>
              <div style={{ display: "flex" }}>
                <User title={""} />
                <label>{executionConfiguration.createdBy}</label>
              </div>
            </FormElement>
          </div>
          <div>
            <FormElement
              labelText={translate("ViewExecution.Name")}>
              <label>{executionConfiguration.name}</label>
            </FormElement>
          </div>
        </Card>
        <Card outline className="summary-view__cards">
          <div>
            <FormElement
              labelText={translate("ViewExecution.Type")}>
              <label>{executionConfiguration.type}</label>
            </FormElement>
          </div>
          {executionConfiguration.dsa?.name &&
              <div>
                <FormElement
                  labelText={translate("ViewExecution.DSA")}>
                  <label>{executionConfiguration.dsa.name}</label>
                </FormElement>
              </div>
          }
        </Card>
        <Card outline className="summary-view__cards">
          <div>
            <FormElement
              labelText={translate("ViewExecution.Included")}>
              <label>{executionConfiguration.counts?.included ?? "-"}</label>
            </FormElement>
          </div>
          <div>
            <FormElement
              labelText={translate("ViewExecution.Excluded")}>
              <label>{executionConfiguration.counts?.excluded ?? "-"}</label>
            </FormElement>
          </div>
        </Card>
        {showingRunDates &&
            <Card outline className="summary-view__cards__dates">
              <div>
                <FormElement
                  labelText={translate("ViewExecution.RelativeRunDate")}>
                  <label>{executionConfiguration.relativeDate}</label>
                </FormElement>
              </div>
              <div>
                <RelativeDatePopup className="popup-types-info-view"/>
              </div>
              <div>
                <FormElement
                  labelText={translate("ViewExecution.DateRun")}>
                  <label>{executionConfiguration.runDate ? executionConfiguration.runDate : "-"}</label>
                </FormElement>
              </div>
              <div>
                <FormElement
                  labelText={translate("ViewExecution.RunTime")}>
                  <label>{executionConfiguration.runtime ? executionConfiguration.runtime : "-"}</label>
                </FormElement>
              </div>
            </Card>
        }
      </Collection>
      {configurationTypeView[executionConfiguration.typeValue]}
    </div>
  );
};
